<template>
  <div class="c-input">
    <div v-if="label" class="c-input-label">
      {{ label }}
    </div>

    <div class="c-input-wrapper">
      <div
        v-if="icon"
        class="c-input-icon"
        :class="{
          'c-input-icon_right': iconRight,
          'c-input-icon_size_small': size === 'small',
        }"
      >
        <svg
          v-if="icon === 'search'"
          class="c-input-icon-svg c-input-icon-svg_stroke"
          width="17"
          height="17"
          viewBox="0 0 17 17"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.4142 11L15.6569 15.2426M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>

        <svg
          v-if="icon === 'calendar'"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.2222 3H2.77778C1.79594 3 1 3.69645 1 4.55556V15.4444C1 16.3036 1.79594 17 2.77778 17H15.2222C16.2041 17 17 16.3036 17 15.4444V4.55556C17 3.69645 16.2041 3 15.2222 3Z"
            stroke="#8E9CBB"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path d="M13 1V5" stroke="#8E9CBB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M5 1V5" stroke="#8E9CBB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <rect x="4" y="8" width="2" height="2" fill="#8E9CBB" />
          <rect x="4" y="12" width="2" height="2" fill="#8E9CBB" />
          <rect x="8" y="8" width="2" height="2" fill="#8E9CBB" />
          <rect x="8" y="12" width="2" height="2" fill="#8E9CBB" />
          <rect x="12" y="8" width="2" height="2" fill="#8E9CBB" />
          <rect x="12" y="12" width="2" height="2" fill="#8E9CBB" />
        </svg>
      </div>

      <input
        v-if="!mask"
        v-model="textValue"
        class="c-input-input"
        :class="{
          'c-input-input_placeholder_italic': placeholderStyle === 'italic',
          'c-input-input_size_small': size === 'small',
          'c-input-input_uneditable': !editable && editable !== undefined,
        }"
        :placeholder="placeholder"
        @focus="focused = true"
        @focusout="focused = false"
        @keydown="keydown"
      />

      <input
        v-if="mask"
        v-model="textValue"
        v-mask="mask"
        class="c-input-input"
        :class="{
          'c-input-input_placeholder_italic': placeholderStyle === 'italic',
          'c-input-input_size_small': size === 'small',
          'c-input-input_uneditable': !editable && editable !== undefined,
        }"
        :placeholder="placeholder"
        @focus="focused = true"
        @focusout="focused = false"
      />

      <div
        class="c-input-actions"
        :class="{
          'c-input-actions_size_small': size === 'small',
        }"
      >
        <div
          v-if="clearable !== undefined"
          class="c-input-action c-input-action_clear"
          :class="{ 'c-input-action_clear_active': textValue }"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg" @click="clear">
            <path
              d="M0.325608 10.746C-0.0649162 10.3555 -0.0649162 9.72233 0.325608 9.33181L9.36312 0.292893C9.75364 -0.0976313 10.3868 -0.0976309 10.7773 0.292893C11.1679 0.683418 11.1679 1.31658 10.7773 1.70711L1.73982 10.746C1.3493 11.1365 0.716132 11.1365 0.325608 10.746Z"
            />
            <path
              d="M10.6836 10.6835C11.0741 10.293 11.0741 9.65986 10.6836 9.26933L1.70711 0.292893C1.31658 -0.0976313 0.683417 -0.0976309 0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L9.26935 10.6835C9.65987 11.0741 10.293 11.0741 10.6836 10.6835Z"
            />
          </svg>
        </div>
      </div>
    </div>

    <div class="c-input-border" :class="{ 'c-input-border_active': focused }"></div>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";

export default {
  name: "c-input",

  directives: { mask },

  props: [
    "value",
    "label",
    "placeholder",
    "icon",
    "placeholderStyle",
    "clearable",
    "iconRight",
    "size",
    "mask",
    "editable",
  ],

  data() {
    return {
      textValue: this.value,

      focused: false,
    };
  },

  watch: {
    textValue(value) {
      this.$emit("input", value);
    },

    value(value) {
      this.textValue = value;
    },
  },

  methods: {
    clear() {
      this.textValue = "";
    },

    keydown(e) {
      if (!this.editable && this.editable !== undefined) {
        e.preventDefault();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.c-input {
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;

  &-label {
    font: {
      size: 12px;
    }
    line-height: 13px;
    margin-bottom: 10px;
    color: $color-label;
  }

  &-wrapper {
    width: 100%;
    display: flex;
  }

  &-input {
    border: none;
    width: 100%;
    font-size: 15px;
    padding: 0 0 13px 0;
    min-height: 30px;
    order: 2;

    &_uneditable {
      caret-color: transparent;
      cursor: pointer;
    }

    &[disabled] {
      background: white;
    }

    &:focus {
      outline: none;

      & + .c-input-border {
        background: $color-primary;
      }
    }

    &::placeholder {
      color: $color-label;
    }

    &_placeholder_italic {
      &::placeholder {
        font-style: italic;
      }
    }

    &_size {
      &_small {
        font-size: 13px;
        padding-bottom: 9px;
        height: 30px;
        padding-top: 8px;
      }
    }
  }

  &-icon {
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    order: 1;
    margin-right: 10px;
    margin-top: -2px;

    &-svg {
      &_stroke {
        stroke: $color-label;
        fill: none;
      }
    }

    &_right {
      order: 5;
      margin-left: 10px;
      margin-right: 0;
    }

    &_size {
      &_small {
        margin-top: 4px;
      }
    }
  }

  &-actions {
    display: flex;
    order: 3;
    align-items: flex-start;

    &_size {
      &_small {
        margin-top: 4px;
      }
    }
  }

  &-action {
    margin-left: 10px;

    &_clear {
      margin-top: -5px;
      transition: 0.2s;
      opacity: 0;
      visibility: hidden;
      cursor: pointer;

      svg {
        fill: $color-secondary;
      }

      &_active {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &-border {
    height: 1px;
    width: 100%;
    position: absolute;
    background: $color-control-border;
    bottom: 0;
    transition: 0.2s;

    &_active {
      background: $color-primary;
    }
  }
}
</style>
